import React, { useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { UserApp } from '../services/api';

interface RegenerateSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: UserApp;
  onConfirm: () => Promise<void>;
}

const RegenerateSecretDialog: React.FC<RegenerateSecretDialogProps> = ({
  open,
  onOpenChange,
  app,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error regenerating secret:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-hidden p-0">
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-yellow-500" />
                Regenerate Secret Key
              </DialogTitle>
              <DialogDescription>
                This action will generate a new secret key for <span className="font-medium">"{app.name}"</span>.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-5 pb-4">
              <Alert className="border-yellow-500/50 bg-yellow-500/10">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                  <strong>Warning:</strong> This action cannot be undone. The current secret key will be invalidated immediately.
                </AlertDescription>
              </Alert>

              <div className="space-y-3 text-sm text-muted-foreground">
                <h4 className="font-medium text-foreground">What happens when you regenerate:</h4>
                <ul className="space-y-2 list-disc list-inside pl-2">
                  <li>A new secret key will be generated</li>
                  <li>The old secret key will be immediately invalidated</li>
                  <li>Any applications using the old key will stop working</li>
                  <li>You'll need to update your integrations with the new key</li>
                </ul>
              </div>

              <div className="bg-muted/50 p-3 rounded border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Current App ID
                </p>
                <code className="text-sm font-mono text-foreground break-all">
                  {app.id}
                </code>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading}
                className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate Secret Key
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegenerateSecretDialog;
